import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001/api/v1';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle 401
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response && error.response.status === 401) {
      const isInIframe = window.parent !== window;
      if (isInIframe) {
        // Trong iframe: xóa token cũ, yêu cầu Portal cấp token mới
        // CrmTokenReceiver sẽ nhận và gọi /auth/iframe để exchange
        localStorage.removeItem('token');
        localStorage.removeItem('_last_portal_token');
        const crmOrigin = process.env.REACT_APP_CRM_ORIGIN || 'http://localhost:3030';
        try {
          window.parent.postMessage({ type: 'REQUEST_CRM_TOKEN' }, crmOrigin);
        } catch (_) {}
      } else {
        // Standalone: redirect về login
        if (!window.location.pathname.includes('/login')) {
          localStorage.removeItem('token');
          window.location.href = '/login';
        }
      }
    }
    return Promise.reject(error);
  }
);

export const authService = {
  login: async (username, password) => {
    const response = await api.post('/auth/login', {
      username,
      password
    });
    if (response.data.access_token) {
      localStorage.setItem('token', response.data.access_token);
    }
    return response.data;
  },
  logout: () => {
    localStorage.removeItem('token');
  },
  googleLogin: async (credential) => {
    const response = await api.post('/auth/google', { credential });
    if (response.data.access_token) {
      localStorage.setItem('token', response.data.access_token);
    }
    return response.data;
  },
  getCurrentUser: async () => {
    const response = await api.get('/auth/me');
    return response.data;
  },
};

export const docService = {
  upload: async (file, documentType, siteId) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('document_type', documentType);
    formData.append('site_id', siteId);
    const response = await api.post('/documents/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },
  getAll: async (params) => {
    const response = await api.get('/documents', { params });
    return response.data; // items, total, etc.
  },
  getOne: async (id) => {
    const response = await api.get(`/documents/${id}`);
    return response.data;
  },
  getDownloadUrl: async (id) => {
    const response = await api.get(`/documents/${id}/download`);
    return response.data.download_url;
  },
  delete: async (id) => {
    const response = await api.delete(`/documents/${id}`);
    return response.data;
  },
  retry: async (id) => {
    const response = await api.post(`/documents/${id}/retry`);
    return response.data;
  },
  retryStuck: async (threshold_minutes = 120) => {
    const response = await api.post('/documents/retry-stuck', null, {
      params: { threshold_minutes },
    });
    return response.data;
  },
  failStuck: async (older_than_days = 7) => {
    const response = await api.post('/documents/fail-stuck', null, {
      params: { older_than_days },
    });
    return response.data;
  },
  forceIndex: async (id) => {
    const response = await api.post(`/documents/${id}/force-index`);
    return response.data;
  },
  fixOcrComplete: async () => {
    const response = await api.post('/documents/fix-ocr-complete');
    return response.data;
  },
  restoreFalseDeleted: async (deletedAfter, deletedBefore) => {
    const params = { deleted_after: deletedAfter };
    if (deletedBefore) params.deleted_before = deletedBefore;
    const response = await api.post('/documents/restore-false-deleted', null, { params });
    return response.data;
  },
  retryProcessing: async (id) => {
    const response = await api.post(`/documents/${id}/retry-processing`);
    return response.data;
  },
  forceFailOne: async (id) => {
    const response = await api.post(`/documents/${id}/force-fail-one`);
    return response.data;
  },
  bulkDownload: async (ids) => {
    const response = await api.post('/documents/bulk-download', { ids }, {
      responseType: 'blob',
    });
    return response.data; // Blob (ZIP archive)
  },
  getEditorConfig: async (id) => {
    const response = await api.get(`/documents/${id}/editor-config`);
    return response.data; // { config, token }
  },
};

export const searchService = {
  search: async (queryObj) => {
    // Extract pagination parameters
    const { page, size, ...bodyParams } = queryObj;
    
    // Send pagination as query params, search params in body
    const response = await api.post('/search', bodyParams, {
      params: {
        page: page || 1,
        size: size || 20
      }
    });
    return response.data;
  },
  autocomplete: async (q) => {
    const response = await api.get('/search/autocomplete', { params: { q } });
    return response.data.options;
  }
};

export const analyticsService = {
  getSummary: async () => {
    const response = await api.get('/analytics/summary');
    return response.data;
  },
  getActivity: async () => {
    const response = await api.get('/analytics/activity');
    return response.data;
  },
};

export const syncService = {
  getStatus: async () => {
    const response = await api.get('/sync/status');
    return response.data;
  },
  getLogs: async (params) => {
    const response = await api.get('/sync/logs', { params });
    return response.data;
  },
};

export const userService = {
  list: async () => {
    const response = await api.get('/auth/users');
    return response.data;
  },
  create: async (data) => {
    const response = await api.post('/auth/users', data);
    return response.data;
  },
  update: async (id, data) => {
    const response = await api.put(`/auth/users/${id}`, data);
    return response.data;
  },
  remove: async (id) => {
    await api.delete(`/auth/users/${id}`);
  },
};

export const settingsService = {
  getAll: async () => {
    const response = await api.get('/settings');
    return response.data;
  },
  batchUpdate: async (changes) => {
    // changes: { key: newValue, ... }
    const response = await api.patch('/settings', { changes });
    return response.data;
  },
};

export const sitesService = {
  list: async () => {
    const response = await api.get('/sites');
    return response.data;
  },
  createSite: async (data) => {
    const response = await api.post('/sites', data);
    return response.data;
  },
  getSiteUsers: async (siteId) => {
    const response = await api.get(`/sites/${siteId}/users`);
    return response.data;
  },
  setUserPermission: async (siteId, userId, allowedDocumentTypes) => {
    const response = await api.put(`/sites/${siteId}/users/${userId}`, {
      allowed_document_types: allowedDocumentTypes,
    });
    return response.data;
  },
  removeUserPermission: async (siteId, userId) => {
    await api.delete(`/sites/${siteId}/users/${userId}`);
  },
};

export const syncSourcesService = {
  list: async () => {
    const response = await api.get('/sync-sources');
    return response.data;
  },
  // Sites + enum values for the create/edit form, so the UI never hardcodes them.
  getOptions: async () => {
    const response = await api.get('/sync-sources/options');
    return response.data;
  },
  create: async (data) => {
    const response = await api.post('/sync-sources', data);
    return response.data;
  },
  update: async (id, changes) => {
    const response = await api.patch(`/sync-sources/${id}`, changes);
    return response.data;
  },
  remove: async (id) => {
    await api.delete(`/sync-sources/${id}`);
  },
  trigger: async (id) => {
    const response = await api.post(`/sync-sources/${id}/trigger`);
    return response.data;
  },
};

export default api;

export const documentTypesService = {
  // include_inactive / with_counts are admin-only views; the plain list is what
  // every picker and filter in the app reads instead of hardcoding options.
  list: async ({ includeInactive = false, withCounts = false } = {}) => {
    const response = await api.get('/document-types', {
      params: { include_inactive: includeInactive, with_counts: withCounts },
    });
    return response.data;
  },
  create: async (data) => {
    const response = await api.post('/document-types', data);
    return response.data;
  },
  update: async (key, changes) => {
    const response = await api.patch(`/document-types/${key}`, changes);
    return response.data;
  },
  remove: async (key) => {
    await api.delete(`/document-types/${key}`);
  },
};
